import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Skeleton,
  useCheckbox,
  useCheckboxGroup,
  useRadio,
  useRadioGroup
} from '@chakra-ui/react';
import type { UseCheckboxProps, UseRadioProps } from '@chakra-ui/react';
import React from 'react';

import type { ReasonKind, ReasonOption } from '../../interface/reasons';
import { splitReasonOptions } from '../../slots/splitReasonOptions';
import type { ReasonLoadState } from './useReasonOptions';

export interface ReasonFieldPalette {
  readonly chipSelected: string;
  readonly border: string;
  readonly inkSoft: string;
  readonly inkMuted: string;
  readonly error: string;
}

export interface ReasonFieldProps {
  readonly kind: ReasonKind;
  readonly idPrefix: string;
  readonly mode: 'single' | 'multi';
  readonly state: ReasonLoadState;
  readonly onRetry: () => void;
  readonly selectedKeys: readonly string[];
  readonly onChange: (keys: string[]) => void;
  readonly isDisabled?: boolean;
  readonly palette: ReasonFieldPalette;
}

export function reasonNoun(kind: ReasonKind): 'hold' | 'exclusion' | 'transfer' {
  if (kind === 'hold') return 'hold';
  if (kind === 'exclude') return 'exclusion';
  return 'transfer';
}

const nextMulti = (current: readonly string[], changed: readonly string[]): string[] => {
  const kept = current.filter((k) => changed.includes(k));
  const added = changed.filter((k) => !current.includes(k));
  return [...kept, ...added];
};

interface PillProps {
  readonly label: string;
  readonly testId: string;
  readonly isChecked: boolean;
  readonly isDisabled?: boolean;
  readonly inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  readonly labelProps: Record<string, unknown>;
  readonly palette: ReasonFieldPalette;
}

const Pill: React.FC<PillProps> = ({
  label,
  testId,
  isChecked,
  isDisabled,
  inputProps,
  labelProps,
  palette
}) => (
  <Box
    as="label"
    data-testid={testId}
    display="inline-flex"
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
  >
    <input {...inputProps} />
    <Box
      as="span"
      {...labelProps}
      textStyle="Body 1"
      display="inline-flex"
      alignItems="center"
      gap="0.375rem"
      px="0.8125rem"
      py="0.4375rem"
      borderRadius="full"
      border="1px solid"
      borderColor={isChecked ? palette.chipSelected : palette.border}
      bg={isChecked ? palette.chipSelected : 'white'}
      color={isChecked ? 'white' : palette.inkSoft}
      opacity={isDisabled ? 0.5 : 1}
    >
      {isChecked && (
        <Box as="span" aria-hidden data-testid={`${testId}-check`}>
          ✓
        </Box>
      )}
      {label}
    </Box>
  </Box>
);

interface RadioPillProps {
  readonly option: ReasonOption;
  readonly testId: string;
  readonly radioProps: UseRadioProps;
  readonly palette: ReasonFieldPalette;
}

const RadioReasonPill: React.FC<RadioPillProps> = ({ option, testId, radioProps, palette }) => {
  const { getInputProps, getLabelProps, state } = useRadio(radioProps);
  return (
    <Pill
      label={option.label}
      testId={testId}
      isChecked={state.isChecked}
      isDisabled={state.isDisabled}
      inputProps={getInputProps()}
      labelProps={getLabelProps()}
      palette={palette}
    />
  );
};

interface CheckboxPillProps {
  readonly option: ReasonOption;
  readonly testId: string;
  readonly checkboxProps: UseCheckboxProps;
  readonly palette: ReasonFieldPalette;
}

const CheckboxReasonPill: React.FC<CheckboxPillProps> = ({
  option,
  testId,
  checkboxProps,
  palette
}) => {
  const { getInputProps, getLabelProps, state } = useCheckbox(checkboxProps);
  return (
    <Pill
      label={option.label}
      testId={testId}
      isChecked={state.isChecked}
      isDisabled={state.isDisabled}
      inputProps={getInputProps()}
      labelProps={getLabelProps()}
      palette={palette}
    />
  );
};

export const ReasonField: React.FC<ReasonFieldProps> = ({
  kind,
  idPrefix,
  mode,
  state,
  onRetry,
  selectedKeys,
  onChange,
  isDisabled,
  palette
}) => {
  const options = state.status === 'loaded' ? state.options : [];
  const split = splitReasonOptions(options);
  const pillKeySet = new Set(split.pills.map((o) => o.key));
  const listKeySet = new Set(split.listOptions.map((o) => o.key));
  const currentChipKeys = selectedKeys.filter((k) => pillKeySet.has(k));
  const currentMenuKeys = selectedKeys.filter((k) => listKeySet.has(k));

  const { getRadioProps: getReasonRadioProps } = useRadioGroup({
    value: selectedKeys[0] ?? '',
    onChange: (next) => onChange([next as string])
  });
  const { getCheckboxProps } = useCheckboxGroup({
    value: currentChipKeys,
    onChange: (next) => {
      const changed = [...(next as string[]), ...currentMenuKeys];
      onChange(nextMulti(selectedKeys, changed));
    }
  });

  if (state.status === 'loading') {
    return (
      <Flex data-testid={`${idPrefix}-reasons-loading`} gap={3}>
        <Skeleton height="2rem" width="6rem" borderRadius="full" />
        <Skeleton height="2rem" width="6rem" borderRadius="full" />
        <Skeleton height="2rem" width="6rem" borderRadius="full" />
      </Flex>
    );
  }

  if (state.status === 'error') {
    return (
      <Alert status="error" data-testid={`${idPrefix}-reasons-error`}>
        <AlertIcon />
        {`Couldn't load ${reasonNoun(kind)} reasons.`}
        <Button
          size="sm"
          ml={3}
          data-testid={`${idPrefix}-reasons-retry`}
          onClick={onRetry}
          isDisabled={isDisabled}
        >
          Try again
        </Button>
      </Alert>
    );
  }

  if (split.pills.length === 0 && split.listOptions.length === 0) {
    return (
      <Alert status="info" data-testid={`${idPrefix}-reasons-empty`}>
        <AlertIcon />
        {`No ${reasonNoun(kind)} reasons are configured. Contact your administrator.`}
      </Alert>
    );
  }

  const otherSelectedCount =
    mode === 'multi'
      ? currentMenuKeys.length
      : selectedKeys.some((k) => listKeySet.has(k))
        ? 1
        : 0;
  const otherChecked = otherSelectedCount > 0;
  const otherLabel = split.other
    ? otherSelectedCount > 0
      ? `${split.other.label} (${otherSelectedCount})`
      : split.other.label
    : '';

  return (
    <Flex
      role={mode === 'single' ? 'radiogroup' : undefined}
      wrap="wrap"
      gap={3}
      align="center"
    >
      {split.pills.map((option) =>
        mode === 'single' ? (
          <RadioReasonPill
            key={option.key}
            option={option}
            testId={`${idPrefix}-reason-${option.key}`}
            radioProps={getReasonRadioProps({ value: option.key, isDisabled })}
            palette={palette}
          />
        ) : (
          <CheckboxReasonPill
            key={option.key}
            option={option}
            testId={`${idPrefix}-reason-${option.key}`}
            checkboxProps={getCheckboxProps({ value: option.key, isDisabled })}
            palette={palette}
          />
        )
      )}
      {split.other && (
        <Menu closeOnSelect={mode !== 'multi'}>
          <MenuButton
            as={Box}
            data-testid={`${idPrefix}-reason-other`}
            textStyle="Body 1"
            display="inline-flex"
            cursor={isDisabled ? 'not-allowed' : 'pointer'}
            px="0.8125rem"
            py="0.4375rem"
            borderRadius="full"
            border="1px solid"
            borderColor={otherChecked ? palette.chipSelected : palette.border}
            bg={otherChecked ? palette.chipSelected : 'white'}
            color={otherChecked ? 'white' : palette.inkSoft}
            opacity={isDisabled ? 0.5 : 1}
            aria-disabled={isDisabled || undefined}
            pointerEvents={isDisabled ? 'none' : undefined}
          >
            {otherLabel}
          </MenuButton>
          <Portal>
            <MenuList zIndex={1400}>
              {mode === 'multi' ? (
                <MenuOptionGroup
                  type="checkbox"
                  value={currentMenuKeys}
                  onChange={(next) => {
                    const changed = [...currentChipKeys, ...(next as string[])];
                    onChange(nextMulti(selectedKeys, changed));
                  }}
                >
                  {split.listOptions.map((option) => (
                    <MenuItemOption
                      key={option.key}
                      value={option.key}
                      data-testid={`${idPrefix}-reason-list-${option.key}`}
                    >
                      {option.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              ) : (
                <MenuOptionGroup
                  type="radio"
                  value={currentMenuKeys[0] ?? ''}
                  onChange={(next) => onChange([next as string])}
                >
                  {split.listOptions.map((option) => (
                    <MenuItemOption
                      key={option.key}
                      value={option.key}
                      data-testid={`${idPrefix}-reason-list-${option.key}`}
                    >
                      {option.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              )}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </Flex>
  );
};
